import network from '@/services/network';
import {
  RoleDefaultUpdateResult,
  RoleDefaultsEntry,
  UserVisibilityResponse,
  WritableVisibilityLevel,
} from '@/types';
import { ErrorResponse } from '@/utils';

/**
 * Visibility-Admin-API (Welle 6.1, Decision #71). Backend-Gate:
 * Permission visibility.manage (SA).
 */

const getRoleDefaults = async (): Promise<RoleDefaultsEntry[]> => {
  try {
    const { data } = await network.get<{ data: RoleDefaultsEntry[] }>(
      '/visibility/role-defaults',
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateRoleDefault = async (
  role: string,
  resourceKey: string,
  level: WritableVisibilityLevel,
): Promise<RoleDefaultUpdateResult> => {
  try {
    const { data } = await network.put<{ data: RoleDefaultUpdateResult }>(
      '/visibility/role-defaults',
      { role, resource_key: resourceKey, visibility_level: level },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getUserVisibility = async (
  userId: number,
): Promise<UserVisibilityResponse> => {
  try {
    const { data } = await network.get<{ data: UserVisibilityResponse }>(
      `/visibility/users/${userId}`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Ohne `resourceKey` schreibt der Aufruf das Beratungsdaten-Buendel
 * (7 Keys, unveraendertes Bestandsverhalten). Mit einem einzelnen Key
 * genau diesen — gebraucht fuer `user`, den Schalter der Manager-Sicht
 * (Decision #160, 13.08.2026).
 *
 * `resource_key` wird nur mitgeschickt, wenn er gesetzt ist: ein
 * `undefined` im Body waere zwar harmlos, aber der Bestands-Aufruf soll
 * Byte fuer Byte derselbe bleiben.
 */
const setUserOverride = async (
  userId: number,
  level: WritableVisibilityLevel,
  resourceKey?: string,
): Promise<UserVisibilityResponse> => {
  try {
    const { data } = await network.put<{ data: UserVisibilityResponse }>(
      `/visibility/users/${userId}/override`,
      {
        visibility_level: level,
        ...(resourceKey ? { resource_key: resourceKey } : {}),
      },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteUserOverride = async (
  userId: number,
): Promise<UserVisibilityResponse> => {
  try {
    const { data } = await network.delete<{ data: UserVisibilityResponse }>(
      `/visibility/users/${userId}/override`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

export interface UnteradminBereich {
  key: string;
  label: string;
  an: boolean;
}

/** Bereichsschalter der Rolle unteradmin (Alex 16.10.2026 20:42), nur SA. */
const getUnteradminBereiche = async (): Promise<UnteradminBereich[]> => {
  try {
    const { data } = await network.get<{ data: UnteradminBereich[] }>(
      '/visibility/unteradmin-bereiche',
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const setUnteradminBereich = async (
  bereich: string,
  an: boolean,
): Promise<UnteradminBereich[]> => {
  try {
    const { data } = await network.put<{ data: UnteradminBereich[] }>(
      '/visibility/unteradmin-bereiche',
      { bereich, an },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Ein Bereich an EINEM Unteradmin: wirksam, Rollen-Standard, Abweichung. */
export interface UnteradminBereichJeUser extends UnteradminBereich {
  rolle: boolean;
  angepasst: boolean;
}

export interface UnteradminBereicheJeUser {
  angepasst: boolean;
  bereiche: UnteradminBereichJeUser[];
}

/**
 * Bereiche je einzelnem Unteradmin (Entscheid Alex 16.10.2026 22:41), nur SA.
 * Wirksamer Wert = Abweichung des Users, sonst Rollen-Standard.
 */
const getUserUnteradminBereiche = async (
  userId: number,
): Promise<UnteradminBereicheJeUser> => {
  try {
    const { data } = await network.get<{ data: UnteradminBereicheJeUser }>(
      `/visibility/users/${userId}/unteradmin-bereiche`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const setUserUnteradminBereich = async (
  userId: number,
  bereich: string,
  an: boolean,
): Promise<UnteradminBereicheJeUser> => {
  try {
    const { data } = await network.put<{ data: UnteradminBereicheJeUser }>(
      `/visibility/users/${userId}/unteradmin-bereiche`,
      { bereich, an },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const resetUserUnteradminBereiche = async (
  userId: number,
): Promise<UnteradminBereicheJeUser> => {
  try {
    const { data } = await network.delete<{ data: UnteradminBereicheJeUser }>(
      `/visibility/users/${userId}/unteradmin-bereiche`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

export default {
  getUserUnteradminBereiche,
  setUserUnteradminBereich,
  resetUserUnteradminBereiche,
  getUnteradminBereiche,
  setUnteradminBereich,
  getRoleDefaults,
  updateRoleDefault,
  getUserVisibility,
  setUserOverride,
  deleteUserOverride,
};
